'use client';
import clsx from 'clsx';
import {useSearchParams} from 'next/navigation';
import {useEffect, useRef, useState} from 'react';
import {validateEditMode} from '../helpers/utils';

export default function EditMode({
  path,
  cmsUrl
}: {
  enable?: boolean;
  path: string;
  cmsUrl: string;
}) {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isShowPanel, setIsShowPanel] = useState<boolean | null>(false);
  const searchParams = useSearchParams();
  const cms_url = cmsUrl.replace(/\/$/, '');
  const [isHashExpired, setIsHashExpired] = useState<boolean>(false);

  const logout = () => {
    if (confirm('Are you sure you want to logout?')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('editmode');
      localStorage.removeItem('editmode');
      setIsAdmin(false);
      setIsDropdownOpen(false);
      window.location.href = url.toString();
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const code = searchParams.get('editmode') ?? '';
    setIsShowPanel(localStorage?.getItem('editmode-show') === '1');
    if (code && code.length > 0) {
      localStorage.setItem('editmode', code);
      if (validateEditMode(code)) {
        setIsAdmin(true);
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('editmode');
      window.history.replaceState({}, '', url.toString());
    }

    setTimeout(() => {
      const hash = localStorage.getItem('editmode') ?? '';
      if (hash) {
        validateEditMode(hash) ? setIsAdmin(true) : setIsHashExpired(true);
      }
    });
  }, []);

  return (
    (isAdmin || isHashExpired) && (
      <>
        <div
          className={clsx(
            'fixed hidden md:flex top-2 right-[50px] z-[1000] gap-2 bg-black/40 rounded-full items-center justify-center p-1 transition-opacity fade-in-up',
            isShowPanel ? 'opacity-100' : 'opacity-0 hover:opacity-100',
            !isAdmin && !isHashExpired && 'hidden'
          )}
        >
          {isAdmin && (
            <div title="Edit">
              <a
                href={cms_url + path}
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="relative w-[30px] h-[30px] cursor-pointer hover:bg-white/10  rounded-full flex items-center justify-center focus:outline-none">
                  <span className="material-symbols-outlined text-white/60 text-base">
                    edit
                  </span>
                </button>
              </a>
            </div>
          )}
          {isHashExpired && (
            <div title="Login">
              <a
                href={cms_url + '/admin/'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="relative w-[30px] h-[30px] cursor-pointer hover:bg-white/10  rounded-full flex items-center justify-center focus:outline-none">
                  <span className="material-symbols-outlined text-white/60 text-base">
                    key
                  </span>
                </button>
              </a>
            </div>
          )}

          <div title={isShowPanel ? 'Hide Panel' : 'Show Panel'}>
            <button
              className="relative w-[30px] h-[30px] cursor-pointer hover:bg-white/10   rounded-full flex items-center justify-center focus:outline-none"
              onClick={() => {
                setIsShowPanel(!isShowPanel);
                localStorage.setItem('editmode-show', isShowPanel ? '0' : '1');
              }}
            >
              {isShowPanel ? (
                <span className="material-symbols-outlined text-white/60 text-base">
                  visibility_lock
                </span>
              ) : (
                <span className="material-symbols-outlined text-white/60 text-base">
                  visibility_off
                </span>
              )}
            </button>
          </div>
          <div className="relative" title="More" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="relative w-[30px] h-[30px] cursor-pointer hover:opacity-70 bg-white/10 rounded-full flex items-center justify-center focus:outline-none"
            >
              <img
                src={'/img/asw-logo-horizontal.png'}
                alt="CMS mode"
                className="w-[30px] h-[30px] rounded-full object-cover bg-white"
              />
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-32 bg-zinc-800 text-white rounded-lg shadow-lg border border-white/10 py-1 flex flex-col z-[1001]">
                <button
                  onClick={logout}
                  className="w-full px-4 py-2 hover:bg-white/10 flex items-center gap-2 text-left text-sm cursor-pointer transition-colors"
                >
                  <span className="material-symbols-outlined text-white/60 text-base">
                    logout
                  </span>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    )
  );
}
